import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { GoogleMap, useLoadScript, Marker, InfoWindow, Polyline } from "@react-google-maps/api";
import api from "../../services/api";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const CENTER_DEFAULT = { lat: 47.2184, lng: -1.5536 }; // Nantes

function Carte() {
  const navigate = useNavigate();
  const [eleves, setEleves] = useState([]);
  const [enseignants, setEnseignants] = useState([]);
  const [cours, setCours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [filter, setFilter] = useState("tous");
  const [showConnections, setShowConnections] = useState(true);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [elevesRes, enseignantsRes, coursRes] = await Promise.all([
        api.get("/eleves"),
        api.get("/enseignants"),
        api.get("/cours?tous=true"),
      ]);
      setEleves(elevesRes.data.filter((e) => e.latitude && e.longitude));
      setEnseignants(enseignantsRes.data.filter((e) => e.latitude && e.longitude));
      setCours(coursRes.data);
    } catch (error) {
      console.error("Erreur:", error);
    } finally {
      setLoading(false);
    }
  };

  // Décaler les marqueurs superposés
  const decalerMarqueurs = (personnes) => {
    const vus = {};
    return personnes.map((p) => {
      const key = `${parseFloat(p.latitude).toFixed(5)}_${parseFloat(p.longitude).toFixed(5)}`;
      if (!vus[key]) {
        vus[key] = 0;
      }
      vus[key]++;
      const index = vus[key] - 1;
      const angle = (index * 45 * Math.PI) / 180;
      const rayon = index === 0 ? 0 : 0.007;
      return {
        ...p,
        latitude: parseFloat(p.latitude) + rayon * Math.cos(angle),
        longitude: parseFloat(p.longitude) + rayon * Math.sin(angle),
      };
    });
  };

  const elevesDecales = decalerMarqueurs(eleves);
  const enseignantsDecales = decalerMarqueurs(enseignants);

  // Créer les lignes de connexion enseignant → élèves
  const connections = [];
  if (showConnections) {
    cours.forEach((c) => {
      const enseignant = enseignants.find((e) => e.id === c.enseignant_id);
      const eleve = eleves.find((e) => e.id === c.eleve_id);
      if (enseignant && eleve) {
        const exists = connections.find(
          (conn) => conn.enseignant_id === enseignant.id && conn.eleve_id === eleve.id
        );
        if (!exists) {
          connections.push({
            enseignant_id: enseignant.id,
            eleve_id: eleve.id,
            path: [
              { lat: parseFloat(enseignant.latitude), lng: parseFloat(enseignant.longitude) },
              { lat: parseFloat(eleve.latitude), lng: parseFloat(eleve.longitude) },
            ],
          });
        }
      }
    });
  }

  // Icônes personnalisées SVG
  const eleveIcon = {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="18" fill="#1565c0" stroke="white" stroke-width="2"/>
        <text x="20" y="26" text-anchor="middle" font-size="18" fill="white">🎓</text>
      </svg>
    `)}`,
    scaledSize: { width: 40, height: 40 },
  };

  const enseignantIcon = {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="18" fill="#6a1b9a" stroke="white" stroke-width="2"/>
        <text x="20" y="26" text-anchor="middle" font-size="18" fill="white">👨‍🏫</text>
      </svg>
    `)}`,
    scaledSize: { width: 40, height: 40 },
  };

  const statsWithCoords = {
    eleves: eleves.length,
    enseignants: enseignants.length,
    connections: connections.length,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>

              {/* Header */}
              <MDBox mx={2} mt={-3} py={3} px={3} sx={{
                background: "linear-gradient(135deg, #1565c0 0%, #6a1b9a 100%)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(21,101,192,0.4)",
                display: "flex", justifyContent: "space-between",
                alignItems: "center", flexWrap: "wrap", gap: 2,
              }}>
                <MDBox>
                  <MDTypography variant="h5" color="white" fontWeight="bold">
                    Carte de l&apos;association
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                    {statsWithCoords.eleves} élève{statsWithCoords.eleves > 1 ? "s" : ""} ·{" "}
                    {statsWithCoords.enseignants} enseignant{statsWithCoords.enseignants > 1 ? "s" : ""} ·{" "}
                    {statsWithCoords.connections} connexion{statsWithCoords.connections > 1 ? "s" : ""}
                  </MDTypography>
                </MDBox>

                {/* Légende */}
                <MDBox display="flex" gap={2} flexWrap="wrap">
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    <MDBox sx={{
                      width: 14, height: 14, borderRadius: "50%",
                      background: "#1565c0", border: "2px solid #fff",
                    }} />
                    <MDTypography variant="caption" color="white">Élèves</MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    <MDBox sx={{
                      width: 14, height: 14, borderRadius: "50%",
                      background: "#6a1b9a", border: "2px solid #fff",
                    }} />
                    <MDTypography variant="caption" color="white">Enseignants</MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    <MDBox sx={{
                      width: 20, height: 2,
                      background: "rgba(255,255,255,0.7)",
                    }} />
                    <MDTypography variant="caption" color="white">Connexions</MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>

              <MDBox p={3}>

                {/* Filtres */}
                <MDBox display="flex" gap={1} mb={2} flexWrap="wrap" alignItems="center">
                  {[
                    { key: "tous", label: "Tous" },
                    { key: "eleves", label: "Élèves seulement" },
                    { key: "enseignants", label: "Enseignants seulement" },
                  ].map((f) => (
                    <MDBox key={f.key} onClick={() => setFilter(f.key)} sx={{
                      padding: "6px 16px", borderRadius: "20px", cursor: "pointer",
                      border: "1px solid",
                      borderColor: filter === f.key ? "#1565c0" : "#d2d6da",
                      background: filter === f.key ? "#1565c0" : "#fff",
                      color: filter === f.key ? "#fff" : "#344767",
                      fontSize: 13, fontWeight: 500, transition: "all 0.2s",
                    }}>
                      {f.label}
                    </MDBox>
                  ))}

                  {/* Toggle connexions */}
                  <MDBox
                    onClick={() => setShowConnections(!showConnections)}
                    sx={{
                      padding: "6px 16px", borderRadius: "20px", cursor: "pointer",
                      border: "1px solid",
                      borderColor: showConnections ? "#6a1b9a" : "#d2d6da",
                      background: showConnections ? "#6a1b9a" : "#fff",
                      color: showConnections ? "#fff" : "#344767",
                      fontSize: 13, fontWeight: 500, transition: "all 0.2s",
                      display: "flex", alignItems: "center", gap: 0.5,
                    }}>
                    <Icon sx={{ fontSize: 14 }}>timeline</Icon>
                    {showConnections ? "Masquer connexions" : "Afficher connexions"}
                  </MDBox>
                </MDBox>

                {/* Statistiques rapides */}
                <MDBox display="flex" gap={2} mb={2} flexWrap="wrap">
                  <MDBox px={2} py={1} sx={{
                    background: "#e3f2fd", borderRadius: "10px",
                    display: "flex", alignItems: "center", gap: 1,
                  }}>
                    <MDBox sx={{
                      width: 10, height: 10, borderRadius: "50%", background: "#1565c0",
                    }} />
                    <MDTypography variant="caption" sx={{ color: "#1565c0", fontWeight: 600 }}>
                      {eleves.length} élève{eleves.length > 1 ? "s" : ""} localisé{eleves.length > 1 ? "s" : ""}
                    </MDTypography>
                  </MDBox>
                  <MDBox px={2} py={1} sx={{
                    background: "#f3e5f5", borderRadius: "10px",
                    display: "flex", alignItems: "center", gap: 1,
                  }}>
                    <MDBox sx={{
                      width: 10, height: 10, borderRadius: "50%", background: "#6a1b9a",
                    }} />
                    <MDTypography variant="caption" sx={{ color: "#6a1b9a", fontWeight: 600 }}>
                      {enseignants.length} enseignant{enseignants.length > 1 ? "s" : ""} localisé{enseignants.length > 1 ? "s" : ""}
                    </MDTypography>
                  </MDBox>
                  {connections.length > 0 && (
                    <MDBox px={2} py={1} sx={{
                      background: "#f8f4ff", borderRadius: "10px",
                      display: "flex", alignItems: "center", gap: 1,
                    }}>
                      <Icon sx={{ fontSize: 14, color: "#6a1b9a" }}>timeline</Icon>
                      <MDTypography variant="caption" sx={{ color: "#6a1b9a", fontWeight: 600 }}>
                        {connections.length} relation{connections.length > 1 ? "s" : ""} enseignant-élève
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>

                {/* Carte */}
                {!isLoaded || loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center"
                    sx={{ height: 500, background: "#f8faff", borderRadius: "12px" }}>
                    <MDTypography variant="body2" color="text">
                      Chargement de la carte...
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #e3f2fd" }}>
                    <GoogleMap
                      mapContainerStyle={{ height: "600px", width: "100%" }}
                      center={CENTER_DEFAULT}
                      zoom={12}
                      options={{
                        streetViewControl: false,
                        mapTypeControl: true,
                        fullscreenControl: true,
                      }}
                    >
                      {/* Lignes de connexion */}
                      {showConnections && connections.map((conn, index) => (
                        <Polyline
                          key={`conn-${index}`}
                          path={conn.path}
                          options={{
                            strokeColor: "#9c27b0",
                            strokeOpacity: 0.8,
                            strokeWeight: 4,
                            geodesic: true,
                            icons: [{
                              icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 3 },
                              offset: "0",
                              repeat: "20px",
                            }],
                          }}
                        />
                      ))}

                      {/* Marqueurs élèves */}
                      {(filter === "tous" || filter === "eleves") && elevesDecales.map((eleve) => (
                        <Marker
                          key={`eleve-${eleve.id}`}
                          position={{
                            lat: parseFloat(eleve.latitude),
                            lng: parseFloat(eleve.longitude),
                          }}
                          icon={eleveIcon}
                          onClick={() => setSelectedMarker({ ...eleve, type: "eleve" })}
                          title={`${eleve.prenom} ${eleve.nom}`}
                        />
                      ))}

                      {/* Marqueurs enseignants */}
                      {(filter === "tous" || filter === "enseignants") && enseignantsDecales.map((ens) => (
                        <Marker
                          key={`ens-${ens.id}`}
                          position={{
                            lat: parseFloat(ens.latitude),
                            lng: parseFloat(ens.longitude),
                          }}
                          icon={enseignantIcon}
                          onClick={() => setSelectedMarker({ ...ens, type: "enseignant" })}
                          title={`${ens.prenom} ${ens.nom}`}
                        />
                      ))}

                      {/* InfoWindow au clic */}
                      {selectedMarker && (
                        <InfoWindow
                          position={{
                            lat: parseFloat(selectedMarker.latitude),
                            lng: parseFloat(selectedMarker.longitude),
                          }}
                          onCloseClick={() => setSelectedMarker(null)}
                        >
                          <div style={{ padding: "8px", minWidth: 160 }}>
                            <div style={{
                              display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
                            }}>
                              <div style={{
                                width: 32, height: 32, borderRadius: "50%",
                                background: selectedMarker.type === "eleve" ? "#1565c0" : "#6a1b9a",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                color: "#fff", fontSize: 16,
                              }}>
                                {selectedMarker.type === "eleve" ? "🎓" : "👨‍🏫"}
                              </div>
                              <div>
                                <div style={{ fontWeight: 700, fontSize: 14, color: "#344767" }}>
                                  {selectedMarker.prenom} {selectedMarker.nom}
                                </div>
                                <div style={{
                                  fontSize: 11,
                                  color: selectedMarker.type === "eleve" ? "#1565c0" : "#6a1b9a",
                                  fontWeight: 600,
                                }}>
                                  {selectedMarker.type === "eleve" ? "Élève" : "Enseignant"}
                                </div>
                              </div>
                            </div>
                            {selectedMarker.adresse && (
                              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 8 }}>
                                📍 {selectedMarker.adresse}, {selectedMarker.ville}
                              </div>
                            )}
                            {selectedMarker.telephone && (
                              <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 8 }}>
                                📞 {selectedMarker.telephone}
                              </div>
                            )}
                            <button
                              onClick={() => navigate(
                                selectedMarker.type === "eleve"
                                  ? `/eleves/${selectedMarker.id}`
                                  : `/enseignants/${selectedMarker.id}`
                              )}
                              style={{
                                width: "100%", padding: "6px",
                                background: selectedMarker.type === "eleve" ? "#1565c0" : "#6a1b9a",
                                color: "#fff", border: "none", borderRadius: 8,
                                cursor: "pointer", fontSize: 12, fontWeight: 600,
                              }}
                            >
                              Voir la fiche →
                            </button>
                          </div>
                        </InfoWindow>
                      )}
                    </GoogleMap>
                  </MDBox>
                )}

                {/* Message si pas de données */}
                {!loading && eleves.length === 0 && enseignants.length === 0 && (
                  <MDBox mt={2} p={2} sx={{
                    background: "#fff3e0", borderRadius: "10px", border: "1px solid #ffe0b2",
                  }}>
                    <MDTypography variant="caption" sx={{ color: "#e65100" }}>
                      ⚠️ Aucun élève ou enseignant n&apos;a d&apos;adresse géolocalisée.
                      Ajoutez des adresses via l&apos;autocomplétion dans les fiches pour les voir apparaître sur la carte.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Carte;